import { useMemo, useRef } from "react";

import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import { t, TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { TSTTable, TST_COLUMN_SIZE } from "@impero-com/ui";
import { createColumnHelper, Table } from "@tanstack/react-table";
import { Popconfirm } from "antd";
import { Control } from "react-hook-form";

import { ControlProgramTagView } from "api/generated/model";

import {
    ControlProgramTagCategoryFormSchema,
    refinedNewNameSchema,
} from "./useControlProgramTagCategoryFormSchema";
import {
    COLUMN_ID_ACTION,
    COLUMN_ID_NAME,
    ControlProgramTagManagementOverview,
    useTable,
} from "./utils";

interface ControlProgramTagManagementListProps {
    control: Control<ControlProgramTagCategoryFormSchema>;
    tags: ControlProgramTagView[];
    handleAddTag: () => Promise<void>;
    handleDeleteTag: (tagId: string) => void;
    existingTagNames: string[];
}
export function ControlProgramTagManagementList({
    control,
    tags,
    handleAddTag,
    handleDeleteTag,
    existingTagNames,
}: ControlProgramTagManagementListProps) {
    const { t } = useTranslation();
    const table = useControlProgramTagManagementListTable(
        control,
        tags,
        handleAddTag,
        handleDeleteTag,
        existingTagNames
    );
    const tableElement = useRef<HTMLDivElement>(null);

    return (
        <TSTTable
            tableRef={tableElement}
            table={table}
            noResultsFallback={t("NoResults")}
            noSourceRowsFallback={t("NoResults")}
        />
    );
}

export function useControlProgramTagManagementListTable(
    control: Control<ControlProgramTagCategoryFormSchema>,
    tags: ControlProgramTagView[],
    handleAddTag: () => Promise<void>,
    handleDeleteTag: (tagId: string) => void,
    existingTagNames: string[]
): Table<ControlProgramTagManagementOverview> {
    const columns = useMemo(
        () =>
            buildColumns(
                t,
                control,
                handleAddTag,
                handleDeleteTag,
                existingTagNames
            ),
        [control, handleAddTag, handleDeleteTag, existingTagNames]
    );

    const table = useTable(tags, columns);
    return table;
}

function buildColumns(
    t: TFunction,
    control: Control<ControlProgramTagCategoryFormSchema>,
    handleAddTag: () => Promise<void>,
    handleDeleteTag: (tagId: string) => void,
    existingTagNames: string[]
) {
    const columnHelper =
        createColumnHelper<ControlProgramTagManagementOverview>();

    return [
        columnHelper.accessor((row) => row.newTagName, {
            id: COLUMN_ID_NAME,
            header: t("TagManagementTable_Column_Name"),
            size: TST_COLUMN_SIZE.M,
            cell: (info) => {
                // Check if this is the first row
                if (info.row.index === 0) {
                    return (
                        <RHFStringInput
                            control={control}
                            name={"newTagName"}
                            schema={refinedNewNameSchema(t, existingTagNames)}
                            placeholder={t(
                                "TagManagementTable_Add_Placeholder"
                            )}
                            onKeyDown={(e) => {
                                if (e.code === "Enter") {
                                    void handleAddTag();
                                }
                            }}
                            autoFocus
                        />
                    );
                }
                return <span>{info.getValue()}</span>;
            },
        }),
        columnHelper.accessor((row) => row.action, {
            id: COLUMN_ID_ACTION,
            header: t("TagManagementTable_Column_Action"),
            size: TST_COLUMN_SIZE.XS,
            cell: (info) => {
                const tagId = info.row.original.tagId;
                if (!tagId) {
                    return (
                        <a onClick={() => void handleAddTag()}>
                            {info.getValue()}
                        </a>
                    );
                }

                return (
                    <Popconfirm
                        title={t(
                            "TagManagementTable_DeleteConfirmation_Title",
                            {
                                tagName: info.row.original.newTagName,
                            }
                        )}
                        onConfirm={() => handleDeleteTag(tagId)}
                        okText={t("TagManagementTable_DeleteConfirmation_Yes")}
                        cancelText={t(
                            "TagManagementTable_DeleteConfirmation_No"
                        )}
                        placement="topRight"
                    >
                        <a>{info.getValue()}</a>
                    </Popconfirm>
                );
            },
        }),
    ];
}
